import { differenceInMilliseconds } from "date-fns";
import { generateReminderString, getTaskContentName, TempTask } from "../../model";
import {
  IStopwatchGarminStream,
  IStopwatchMovesenseStream,
  ITask,
  TaskType,
} from "../../shared/db";
import { useSelectTheme } from "../../store";
import {
  BellIcon,
  DeviceIcon,
  MovesenseImg,
  ParticipantTaskIcon,
  RepeatIcon,
  SurveyBlackIcon,
  TimerBlackIcon,
} from "../../asset/image";

import { useCreateTaskColorTranslator, RIF, setAlpha } from "../../lib";
import { convertGarminStreamDescription, convertMovesenseDescription, TaskScheduleCard } from "..";
import { omit } from "lodash";

interface TaskCardProps<T extends ITask | TempTask> {
  item: T;
  index: number;
}

export const TaskCard = <T extends ITask | TempTask>(props: TaskCardProps<T>) => {
  const { color, fontSize, fontWeight } = useSelectTheme();
  const colorTranslator = useCreateTaskColorTranslator();
  const { item, index } = props;
  const taskContentName = getTaskContentName(item.type);
  const content = item[taskContentName];
  const sortedScheduleList = [...(item.scheduleList ?? [])]
    .filter((schedule) => schedule.current)
    .sort((a, b) => {
      return differenceInMilliseconds(new Date(b.createdAt), new Date(a.createdAt));
    });

  const taskTagStyle: React.CSSProperties = {
    border: `1px solid ${color.grey_160}`,
    borderRadius: "5px",
    borderLeft: item.color ? `6px solid #${item.color}` : `6px solid ${colorTranslator(index)}`,
    padding: "8px",
    gap: "8px",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    width: "100%",
    backgroundColor: color.white,
  };

  const eventTrackerTaskTagStyle: React.CSSProperties = {
    ...omit(taskTagStyle, "borderLeft", "border", "background"),
    border: `1px solid #${item.color}`,
    background: setAlpha(`#${item.color}`, 0.08),
  };

  return (
    <div
      key={item?.id}
      css={{
        ...(item.eventTracker ? eventTrackerTaskTagStyle : taskTagStyle),
      }}
    >
      <div
        css={{ display: "flex", gap: "8px", alignItems: "center", justifyContent: "flex-start" }}
      >
        <img
          width="20"
          height="20"
          src={
            item.type === TaskType.Todo
              ? ParticipantTaskIcon
              : item.type === TaskType.Timer
                ? TimerBlackIcon
                : item.type === TaskType.Questionnaire
                  ? SurveyBlackIcon
                  : item.type === TaskType.StopwatchGarminStream
                    ? DeviceIcon
                    : MovesenseImg
          }
          alt={`${item.type} icon to indicate task`}
          css={{
            transform:
              item.type === TaskType.StopwatchMovesenseStream ||
              item.type === TaskType.StopwatchMovesenseLogData
                ? "scale(1.2)"
                : "scale(1)",
          }}
        />
        {RIF(content?.name, (name) => (
          <span css={{ fontSize: fontSize.h7, fontWeight: fontWeight.thick }}>{name}</span>
        ))}
      </div>
      {RIF(
        item.scheduleRepeat || item.scheduleDescription,
        <div css={{ display: "flex", alignItems: "center", gap: "3px" }}>
          {RIF(
            item.scheduleRepeat,
            <img src={RepeatIcon} width="14" alt="Repeat icon" aria-hidden="true" />,
          )}
          {RIF(item.scheduleDescription, (scheduleDescription) => (
            <span css={{ fontSize: "12px", color: color.grey_400 }}>{scheduleDescription}</span>
          ))}
        </div>,
      )}
      {RIF(
        item.type === TaskType.StopwatchMovesenseStream ||
          item.type === TaskType.StopwatchMovesenseLogData ||
          item.type === TaskType.StopwatchGarminStream,
        <div
          css={{
            background: color.background,
            borderRadius: "5px",
            padding: "4px 8px",
            color: color.grey_600,
            fontSize: fontSize.h8,
          }}
        >
          {RIF(
            item.type === TaskType.StopwatchMovesenseStream ||
              item.type === TaskType.StopwatchMovesenseLogData,
            convertMovesenseDescription(content as IStopwatchMovesenseStream),
          )}
          {RIF(
            item.type === TaskType.StopwatchGarminStream,
            convertGarminStreamDescription(content as IStopwatchGarminStream),
          )}
        </div>,
      )}
      {RIF(
        sortedScheduleList.length,
        <div
          css={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: "8px",
          }}
        >
          {sortedScheduleList.map((schedule) => (
            <TaskScheduleCard
              key={schedule.id}
              schedule={schedule}
              isViewOnly={true}
              style={{
                border: `1px solid ${color.border.default}`,
                borderRadius: "5px",
                cursor: "default",
              }}
            />
          ))}
        </div>,
      )}
      {item.reminderList?.map((reminder, index) => (
        <div
          key={index}
          css={{
            background: color.background,
            borderRadius: "5px",
            padding: "4px 8px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <img src={BellIcon} width="16" alt="Reminder icon" aria-hidden="true" />
          {RIF(generateReminderString(reminder), (reminderString) => (
            <p css={{ color: color.textIcon.secondary, fontSize: fontSize.h8 }}>{reminderString}</p>
          ))}
        </div>
      ))}
    </div>
  );
};
